import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SpeakToYourAgentCardShort from 'components/SpeakToYourAgentCardShort';
import Layout from 'components/pageLayout/Layout';
import PageContentContainer from 'components/pageLayout/PageContentContainer';
import { getMe } from 'services/ApiService';
import { getLatestBookingsAndQuotes } from 'services/CommonService';
import { setLatestBookings, setLatestQuotes, toggleLoadingAppOverlay } from 'reducers/ui/uiSlice';
import { setUserData } from 'reducers/currentUser/currentUserSlice';
import { getFullname } from 'shared/helpers/common';
import Bookings from './Bookings';
import Bundles from './Bundles';
import Details from './Details';
import Header from './Header';
import Passport from './Passport';
import Quotes from './Quotes';

export default function OverviewPage() {
  const { agent } = useSelector((state) => state.currentUser);
  const { componentConfig, latestBookings, latestQuotes, isInitialised, loggedOutFromTimeout } = useSelector(
    (state) => state.ui,
  );
  const { userData } = useSelector((state) => state.currentUser);
  const dispatch = useDispatch();

  const loadData = useCallback(async () => {
    try {
      const { data: { bookings } = {} } = await getLatestBookingsAndQuotes(
        '_sort=outbound',
      );
      const { data: { quotes } = {} } = await getLatestBookingsAndQuotes(
        '_sort=-created',
      );
      dispatch(setLatestBookings(bookings?.results || []));
      dispatch(setLatestQuotes(quotes?.results || []));

      const { data: user } = await getMe();
      dispatch(setUserData(user));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialised) {
      loadData();
    }
  }, [isInitialised, loadData]);

  useEffect(() => {
    if (!loggedOutFromTimeout) {
      dispatch(toggleLoadingAppOverlay(false));
    }
  }, [loggedOutFromTimeout, dispatch]);

  // Separate bookings into bundles (loyalty) and regular bookings
  const { bundles, regularBookings } = useMemo(() => {
    const loyaltyBookings = latestBookings?.filter(booking => 
      booking.tours?.some(tour => tour.loyalty === true)
    ) || [];
    const nonLoyaltyBookings = latestBookings?.filter(booking =>
      !booking.tours?.some(tour => tour.loyalty === true)
    ) || [];
    return { bundles: loyaltyBookings, regularBookings: nonLoyaltyBookings };
  }, [latestBookings]);

  const { first: firstName, last: lastName } = userData?.fullName || {};

  return (
    <Layout>
      <Header
        clientDisplayName={getFullname({ firstName, lastName })}
        agentFullName={agent?.fullName}
        quotes={latestQuotes}
      />
      <PageContentContainer>
        <div className="lg:flex md:items-start mx-4 mb-[40px] grid:mx-0">
          <div className="grow lg:mr-[40px]">
            {bundles?.length > 0 && componentConfig.bookingsEnabled &&
              <Bundles bundles={bundles} />
            }
            {regularBookings?.length > 0 && componentConfig.bookingsEnabled &&
              <Bookings bookings={regularBookings} />
            }
            {latestQuotes?.length > 0 && componentConfig.quotesEnabled &&
              <Quotes quotes={latestQuotes} />
            }
            {componentConfig.documentsEnabled &&
              <Passport />
            }
            <Details userData={userData} />
          </div>
          {componentConfig.contactAgentEnabled &&
            <SpeakToYourAgentCardShort />
          }
        </div>
      </PageContentContainer>
    </Layout>
  );
}
